// Comercial › Clientes: el DIRECTORIO (búsqueda en el servidor, paginada). Enter o doble clic abre la
// MISMA Ficha Comercial 360 que Ctrl+K: no hay una segunda ficha (ronda 1).
import { useState } from 'react';
import { useBuscarClientes, useDebounce } from '../api/consultas';
import type { ClienteEncontrado } from '../api/contratos';
import { Grilla, type Columna } from '../comun/Grilla';
import { PanelError } from '../comun/Estados';
import { NOMBRE_CONDICION, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useAtajos } from '../shell/atajos';
import { ClienteNuevo } from './ClienteNuevo';

const COLUMNAS: Columna<ClienteEncontrado>[] = [
  {
    id: 'rut',
    titulo: 'RUT',
    celda: (c) => <span className="mono">{rut(c.rut, c.dv)}</span>,
    ancho: '9rem',
  },
  { id: 'razon', titulo: 'Razón social', celda: (c) => c.razon_social },
  { id: 'fantasia', titulo: 'Nombre', celda: (c) => c.nombre_fantasia ?? '' },
  {
    id: 'condicion',
    titulo: 'Condición',
    celda: (c) => NOMBRE_CONDICION[c.condicion_pago ?? ''] ?? c.condicion_pago ?? '—',
    ancho: '8rem',
  },
  {
    id: 'estado',
    titulo: 'Estado',
    ancho: '8rem',
    celda: (c) =>
      c.bloqueado ? (
        <span className="insignia tono-malo">Bloqueado</span>
      ) : (
        <span className={`insignia tono-${c.estado === 'activa' ? 'ok' : 'aviso'}`}>
          {c.estado}
        </span>
      ),
  },
];

export function ConsultaClientes({
  activa,
  alAbrirCliente,
}: {
  activa: boolean;
  alAbrirCliente: (c: ClienteEncontrado) => void;
}) {
  const { empresa, puede } = useSesion();
  const [texto, setTexto] = useState('');
  const [nuevo, setNuevo] = useState(false);
  // Crear un cliente sin tener que digitar un RUT inexistente en la nota (Rodrigo, 2026-10-24).
  const puedeCrear = puede('entidades.gestionar') && puede('entidades.global.gestionar');
  useAtajos(activa && !nuevo, { 'Alt+U': () => puedeCrear && setNuevo(true) });
  const q = useBuscarClientes(empresa!.id, useDebounce(texto));
  // El alta se hace en la forma de la ficha, en esta misma pantalla; al crear, abre su ficha.
  if (nuevo)
    return (
      <ClienteNuevo
        enPantalla
        empresaId={empresa!.id}
        rutInicial=""
        dvInicial={null}
        alCreado={(c) => {
          setNuevo(false);
          alAbrirCliente(c);
        }}
        alCerrar={() => setNuevo(false)}
      />
    );
  return (
    <div className="pantalla consulta" data-pantalla="clientes">
      <div className="barra-filtros">
        <input
          autoFocus={activa}
          className="buscador"
          aria-label="Buscar clientes"
          placeholder="RUT, razón social o nombre… (↓ para ir a la lista)"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              (
                e.currentTarget.parentElement?.nextElementSibling?.querySelector(
                  'table',
                ) as HTMLElement | null
              )?.focus();
            }
          }}
        />
        {puedeCrear && (
          <button type="button" className="primario" onClick={() => setNuevo(true)}>
            + Cliente nuevo (Alt+U)
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Clientes"
        columnas={COLUMNAS}
        filas={q.filas}
        clave={(c) => c.id}
        cargando={q.isFetching}
        mas={{ hayMas: q.hayMas, cargando: q.cargandoMas, alCargar: q.cargarMas }}
        vacio={texto.trim().length < 2 ? 'Escriba al menos 2 caracteres' : 'Sin resultados'}
        alAbrir={alAbrirCliente}
      />
      <p className="ayuda">↑ ↓ elegir · Enter o doble clic: abrir la Ficha Comercial 360</p>
    </div>
  );
}
